import type { PriceListApiResponse } from '../price-lists/types';
import { PRICE_LIST_SOURCES } from '../price-lists/types';
import type { VariantPriceInput, VariantPricing } from './types';

export const EDITOR_ERRORS = {
  DUPLICATE_QUANTITY: 'duplicate_quantity',
  UNIT_OF_MEASURE_NEEDS_QUANTITY_ONE: 'unit_of_measure_needs_quantity_one',
} as const;

export type EditorError = (typeof EDITOR_ERRORS)[keyof typeof EDITOR_ERRORS];

/** A price list is editable only when it is not synchronised from a platform or integration. */
export function canEditPriceList(priceList: PriceListApiResponse | undefined): boolean {
  return (
    priceList?.source !== PRICE_LIST_SOURCES.INTEGRATION &&
    priceList?.source !== PRICE_LIST_SOURCES.PLATFORM
  );
}

/** Whether any price in the list carries a unit of measure. */
export function mustSetUnitOfMeasure(prices: VariantPriceInput[]): boolean {
  return prices.some((price) => price.unit_of_measure);
}

/**
 * Whether a unit of measure applies to the list at all. It stops applying once the list holds two
 * or more prices and none of them carries one, as no row can set it from then on.
 */
export function isUnitOfMeasureAvailable(prices: VariantPriceInput[]): boolean {
  return mustSetUnitOfMeasure(prices) || prices.length < 2;
}

/**
 * The unit of measure input is disabled when no unit of measure applies to the list, or on the
 * quantity 1 row (which is always the base price).
 */
export function isUnitOfMeasureDisabled(
  prices: VariantPriceInput[],
  price: VariantPriceInput,
): boolean {
  return !isUnitOfMeasureAvailable(prices) || price.quantity === 1;
}

/**
 * Whether the unit of measure field is required for a row. New prices follow the list's rule;
 * editing an existing price keeps it required only when that price already had a unit of measure.
 */
export function isUnitOfMeasureRequired(
  prices: VariantPriceInput[],
  price: VariantPriceInput,
  isNew: boolean,
): boolean {
  return isNew
    ? mustSetUnitOfMeasure(prices)
    : Boolean(price.unit_of_measure) && mustSetUnitOfMeasure(prices);
}

/** Quantity for the next blank draft row, one above the highest saved or drafted quantity. */
export function nextDraftQuantity(
  prices: VariantPriceInput[],
  drafts: VariantPriceInput[],
): number {
  const lastPrice = prices.length > 0 ? (prices[prices.length - 1]?.quantity ?? 0) : 0;
  const maxDraft =
    drafts.length > 0 ? Math.max(...drafts.map((draft) => draft.quantity)) : lastPrice;
  return Math.max(lastPrice, maxDraft) + 1;
}

/** Validation errors that block saving a brand new price, or `null` when it is valid. */
export function validateNewPrice(
  prices: VariantPriceInput[],
  draft: VariantPriceInput,
): EditorError | null {
  const hasQuantityOne = prices.some((price) => price.quantity === 1);
  if (draft.unit_of_measure && !hasQuantityOne) {
    return EDITOR_ERRORS.UNIT_OF_MEASURE_NEEDS_QUANTITY_ONE;
  }
  if (prices.some((price) => price.quantity === draft.quantity)) {
    return EDITOR_ERRORS.DUPLICATE_QUANTITY;
  }
  return null;
}

/** Validation errors that block saving an edited price, or `null` when it is valid. */
export function validateEditedPrice(
  prices: VariantPriceInput[],
  index: number,
  edited: VariantPriceInput,
): EditorError | null {
  const original = prices[index];
  if (
    original &&
    edited.quantity !== original.quantity &&
    prices.some((price) => price.quantity === edited.quantity)
  ) {
    return EDITOR_ERRORS.DUPLICATE_QUANTITY;
  }
  return null;
}

/** Custom price lists that are not already priced for the variant. */
export function availablePriceListsToAdd(
  priceLists: PriceListApiResponse[],
  pricing: VariantPricing[],
): PriceListApiResponse[] {
  return priceLists.filter(
    (list) =>
      list.source !== PRICE_LIST_SOURCES.INTEGRATION &&
      list.source !== PRICE_LIST_SOURCES.PLATFORM &&
      !pricing.some((pricedList) => pricedList.price_list_slug === list.slug),
  );
}

/**
 * Merge the authoritative PATCH response into the current pricing. Each entry replaces the list
 * with the same slug, drops the list when its pricing is empty, and appends when it is new.
 */
export function mergePricingResponse(
  current: VariantPricing[] | undefined,
  response: VariantPricing[],
): VariantPricing[] {
  const merged = current ? [...current] : [];
  for (const entry of response) {
    const index = merged.findIndex((list) => list.price_list_slug === entry.price_list_slug);
    if (entry.pricing.length === 0) {
      if (index !== -1) {
        merged.splice(index, 1);
      }
    } else if (index !== -1) {
      merged[index] = { ...entry };
    } else {
      merged.push({ ...entry });
    }
  }
  return merged;
}
